import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import AppShell from '../components/AppShell';
import SignaturePad from '../components/SignaturePad';
import WireInstructionsModal from '../components/WireInstructionsModal';
import EsignConsentGate from '../components/EsignConsentGate';
import useBodyScrollLock from '../hooks/useBodyScrollLock';
import { CD_NOT_SCHEDULED_STATE, cdNotScheduledLine } from '../constants/cdNotScheduledCopy';
import { useAuth } from '../context/AuthContext';
import { cdPdfLoadKey } from '../utils/cdPdfLoadKey';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const fmtMoney = (n) => {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return `$${Math.round(Number(n)).toLocaleString()}`;
};
const fmtMoneyPerMo = (n) => {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return `$${Math.round(Number(n)).toLocaleString()}/mo`;
};
const fmtPct = (n) => {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return `${Number(n).toFixed(3).replace(/\.?0+$/, '')}%`;
};

export default function ClosingDisclosures() {
  const { token } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cdModal, setCdModal] = useState(false);
  useBodyScrollLock(cdModal);   // the page behind stays put; the popup's body scrolls (index.css .safe-modal-body)
  const [cdCheck1, setCdCheck1] = useState(false);
  const [cdCheck2, setCdCheck2] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signError, setSignError] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [showWireCTC, setShowWireCTC] = useState(false);
  const [wireSending, setWireSending] = useState(false);
  const [openSection, setOpenSection] = useState(null);

  // CD PDF viewer state — blob URL is the iframe src. Released on
  // unmount so we don't leak the object URL across navigations.
  const [cdPdfUrl, setCdPdfUrl]       = useState(null);
  const [cdPdfLoading, setCdPdfLoading] = useState(true);
  const [cdPdfError, setCdPdfError]   = useState('');
  const [needsEsignConsent, setNeedsEsignConsent] = useState(false);
  const [cdReloadKey, setCdReloadKey] = useState(0);
  const cdPdfUrlRef = useRef(null);

  // Sign-modal state — canvas signature data URL + typed legal name.
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  const [typedName, setTypedName] = useState('');

  // Single source: /api/closing/status (the same endpoint the Dashboard
  // closing hero reads). It carries cd_reviewed, cd_signed, wire state,
  // wire_instructions, cash_to_close and the loan_terms block needed
  // for the accordion — so the two surfaces stay in sync without a
  // second fetch.
  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/closing/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch closing data', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const pdfKey = cdPdfLoadKey(data, loading);
  // Load the CD PDF as a blob, store the object URL for the iframe.
  // Release on unmount so the blob doesn't pin memory across navs.
  useEffect(() => {
    let cancelled = false;
    const loadCdPdf = async () => {
      setCdPdfLoading(true); setCdPdfError('');
      try {
        const res = await axios.get(`${API_URL}/api/closing/cd-pdf`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        });
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        cdPdfUrlRef.current = url;
        setCdPdfUrl(url);
      } catch (err) {
        if (cancelled) return;
        // 🚨 451 IS E-SIGN CONSENT — see the twin in OfferAcceptedReview. Left
        // to the generic branch it renders as "Unable to load Closing
        // Disclosure", which tells the borrower nothing they can act on.
        if (err.response?.status === 451) {
          setNeedsEsignConsent(true);
        } else if (err.response?.status === 404) {
          setCdPdfError('Your Closing Disclosure is still being prepared. Refresh in a minute.');
        } else {
          setCdPdfError(err.response?.data?.error || 'Unable to load Closing Disclosure.');
        }
      } finally {
        if (!cancelled) setCdPdfLoading(false);
      }
    };
    if (token && pdfKey !== null) loadCdPdf();
    return () => {
      cancelled = true;
      if (cdPdfUrlRef.current) {
        URL.revokeObjectURL(cdPdfUrlRef.current);
        cdPdfUrlRef.current = null;
      }
    };
  // cdReloadKey: bumped after consent is granted so the refused fetch is retried.
  // pdfKey (utils/cdPdfLoadKey): one load once the status has answered, and again once signed (the signed copy).
  }, [token, pdfKey, cdReloadKey]);

  const handleReviewCD = async () => {
    setReviewing(true);
    try {
      await axios.post(
        `${API_URL}/api/closing/review-cd`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchData();
    } catch (err) {
      console.error('Failed to mark CD reviewed', err);
    } finally {
      setReviewing(false);
    }
  };

  const handleSignCD = async () => {
    setSigning(true);
    setSignError('');
    try {
      await axios.post(
        `${API_URL}/api/closing/sign-cd`,
        { signatureDataUrl, typedName: typedName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchData();
      setCdModal(false);
      setCdCheck1(false);
      setCdCheck2(false);
      setSignatureDataUrl(null);
      setTypedName('');
    } catch (err) {
      setSignError(err.response?.data?.error || 'Failed to sign the Closing Disclosure. Please try again.');
    } finally {
      setSigning(false);
    }
  };

  // Mirrors Dashboard.handleCtcSent: POST /api/closing/wire-sent flips
  // closing_disclosures.wire_sent=1 + payment_method='wire' + records
  // the cash_to_close_amount. Both surfaces read from the same column,
  // so the Dashboard checklist's ctcSent flag flips the same moment.
  const handleWireSent = async () => {
    setWireSending(true);
    try {
      const amount = data?.net_cash_to_close
        ?? data?.cd_cash_to_close
        ?? data?.cash_to_close_amount
        ?? null;
      await axios.post(
        `${API_URL}/api/closing/wire-sent`,
        { amount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchData();
    } catch (err) {
      console.error('Failed to confirm wire', err);
    } finally {
      setWireSending(false);
    }
  };

  const toggleSection = (section) => {
    setOpenSection((prev) => (prev === section ? null : section));
  };

  const cd_reviewed = data?.cd_reviewed;
  const cd_signed = data?.cd_signed;
  const wire_sent = data?.ctc_wire_sent;
  const funds_received = data?.ctc_wire_verified;
  const lt = data?.loan_terms || {};
  // Cash-to-close breakdown — mirrors the Dashboard "Cash to Close
  // Breakdown" panel. Gross = total cash to close from CD (or atlas
  // fallback). dpSent = down payment the borrower wired during option
  // period (only counted once admin verifies receipt). Net = what they
  // still owe at closing. We display gross + the deduction + net so
  // the borrower understands the credit; the "Amount Due" headline is
  // the net so it matches the figure they actually need to wire.
  const grossCtc = data?.cd_cash_to_close ?? data?.cash_to_close_amount ?? lt.cash_to_close ?? null;
  const dpSent = data?.down_payment_verified ? Number(data?.down_payment_amount_sent || 0) : 0;
  const netCtc = data?.net_cash_to_close ?? (grossCtc != null ? Math.max(0, Number(grossCtc) - dpSent) : null);
  const ctcAmount = netCtc ?? grossCtc;

  const accordionSections = [
    {
      id: 'loan-terms',
      title: 'Loan Terms',
      rows: [
        { label: 'Loan Amount',   value: fmtMoney(lt.loan_amount) },
        { label: 'Loan Type',     value: lt.loan_type || 'Fixed Rate' },
        { label: 'Loan Term',     value: lt.loan_term || '30 years' },
        { label: 'Interest Rate', value: fmtPct(lt.interest_rate_pct) },
      ],
    },
    {
      id: 'projected-payments',
      title: 'Projected Payments',
      rows: [
        { label: 'Principal & Interest', value: fmtMoneyPerMo(lt.monthly_pi) },
        // (Mortgage Insurance row removed — MI not bound on any loan.)
        // Estimated Escrow row — mirrors the CD's Projected Payments
        // box (property tax + HOI monthly). Without it the borrower's
        // accordion total didn't reconcile with the CD's monthly total.
        ...(lt.monthly_escrow
          ? [{ label: 'Estimated Escrow', value: fmtMoneyPerMo(lt.monthly_escrow) }]
          : []),
        { label: 'Total Monthly Payment', value: fmtMoneyPerMo(lt.monthly_total) },
      ],
    },
    {
      id: 'cash-to-close',
      title: 'Cash to Close',
      rows: [
        { label: 'Purchase Price',      value: fmtMoney(lt.loan_amount && lt.down_payment ? Number(lt.loan_amount) + Number(lt.down_payment) : null) },
        { label: 'Down Payment',        value: fmtMoney(lt.down_payment) },
        { label: 'Total Cash to Close', value: fmtMoney(grossCtc) },
        ...(dpSent > 0
          ? [
              { label: 'Down Payment Already Sent', value: `−${fmtMoney(dpSent)}` },
              { label: 'Remaining to Wire',         value: fmtMoney(netCtc) },
            ]
          : []),
      ],
    },
  ];

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-black mb-2">Closing Disclosure</h1>
          <p className="text-gray-500">Review your final loan terms and prepare for closing day</p>
        </div>

        {/* Federal Law Notice Banner */}
        <div
          className="rounded-xl p-4 mb-6 text-black text-sm font-medium"
          style={{ backgroundColor: '#fffd5b' }}
        >
          Federal law requires you receive your Closing Disclosure at least 3 business days before closing. Please review carefully.
        </div>

        {/* 🔑 NO CLOSING DATE YET (GET /api/closing/status → state 'closing_not_scheduled'): the approved line
            (constants/cdNotScheduledCopy); while it is unapproved, the page's neutral loading line instead. */}
        {loading || (data && data.state === CD_NOT_SCHEDULED_STATE && !cdNotScheduledLine()) ? (
          <div className="text-center py-16 text-gray-400">Loading closing details…</div>
        ) : data && data.state === CD_NOT_SCHEDULED_STATE ? (
          <div data-testid="cd-not-scheduled" className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 text-sm text-black">
            {cdNotScheduledLine()}
          </div>
        ) : (
          <div className="space-y-6">

            {/* TASK 1 Review Closing Disclosure */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h2 className="text-lg font-bold text-black">Review Closing Disclosure (CD)</h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Your Closing Disclosure shows your final loan terms, monthly payment, and all closing costs. Compare with your Loan Estimate.
                  </p>
                </div>
                <span
                  className={`ml-4 shrink-0 text-xs font-semibold px-3 py-1 rounded-full ${
                    cd_reviewed
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {cd_reviewed ? 'Reviewed ✓' : 'Pending Review'}
                </span>
              </div>

              {/* Embedded CD PDF — the real document from documents.file_data
                  served via GET /api/closing/cd-pdf. */}
              <div className="mt-4">
                {needsEsignConsent ? (
                  <EsignConsentGate
                    token={token}
                    documentLabel="your Closing Disclosure"
                    onConsented={() => { setNeedsEsignConsent(false); setCdReloadKey((k) => k + 1); }}
                  />
                ) : cdPdfLoading ? (
                  <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-12 text-center text-sm text-gray-500">
                    Loading your Closing Disclosure…
                  </div>
                ) : cdPdfUrl ? (
                  <iframe
                    title="Closing Disclosure"
                    src={`${cdPdfUrl}#toolbar=1&navpanes=0&zoom=75`}
                    style={{ width: '100%', height: '60vh', minHeight: 400, border: '1px solid #e5e7eb', borderRadius: 12 }}
                  />
                ) : (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-6 text-sm text-amber-800">
                    {cdPdfError || 'Your Closing Disclosure is being prepared. Please check back shortly.'}
                  </div>
                )}
              </div>

              {/* CD Breakdown Accordion — values pulled from
                  /api/closing/status.loan_terms (real fields from users
                  + closing_disclosures: loan_amount, approved_interest_rate,
                  computed monthly P&I, atlas_cash_to_close). */}
              <div className="mt-6 space-y-2">
                {accordionSections.map((section) => (
                  <div key={section.id} className="border border-gray-200 rounded-xl overflow-hidden">
                    <button
                      className="w-full flex items-center justify-between px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
                      onClick={() => toggleSection(section.id)}
                    >
                      <span className="font-semibold text-sm text-black">{section.title}</span>
                      <svg
                        className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
                          openSection === section.id ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {openSection === section.id && (
                      <div className="px-4 py-3 bg-white space-y-2">
                        {section.rows.map((row) => (
                          <div key={row.label} className="flex justify-between text-sm">
                            <span className="text-gray-500">{row.label}</span>
                            <span className="font-semibold text-black">{row.value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Review Button */}
              <div className="mt-6">
                <button
                  onClick={handleReviewCD}
                  disabled={!!cd_reviewed || reviewing}
                  className="w-full py-3 rounded-xl text-white font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: cd_reviewed ? '#9ca3af' : '#3a6ad9' }}
                >
                  {reviewing ? 'Marking as Reviewed…' : cd_reviewed ? 'Already Reviewed ✓' : 'I Have Reviewed the CD'}
                </button>
              </div>
            </div>

            {/* TASK 2 E-Sign Closing Disclosure */}
            <div
              className={`rounded-2xl border border-gray-200 shadow-sm p-6 ${
                !cd_reviewed ? 'bg-gray-50' : 'bg-white'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h2 className="text-lg font-bold text-black">E-Sign Closing Disclosure</h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Electronically sign your Closing Disclosure to acknowledge you have reviewed your final loan terms.
                  </p>
                </div>
                <span
                  className={`ml-4 shrink-0 text-xs font-semibold px-3 py-1 rounded-full ${
                    cd_signed
                      ? 'bg-green-100 text-green-700'
                      : cd_reviewed
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {cd_signed ? 'Signed ✓' : cd_reviewed ? 'Ready to Sign' : 'Locked'}
                </span>
              </div>

              {!cd_reviewed ? (
                <div className="text-sm text-gray-400 mt-2">Complete the review above to unlock.</div>
              ) : (
                <button
                  onClick={() => setCdModal(true)}
                  disabled={!!cd_signed}
                  className="mt-4 w-full py-3 rounded-xl text-white font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: cd_signed ? '#9ca3af' : '#3a6ad9' }}
                >
                  {cd_signed ? 'Signed ✓' : 'Sign Closing Disclosure →'}
                </button>
              )}
            </div>

            {/* TASK 3 Pay Cash to Close — Dashboard-parity layout: two
                buttons (View Wire Instructions + I've Sent the Wire),
                no payment-method picker. Same /api/closing/wire-sent
                endpoint Dashboard.handleCtcSent calls, so flipping
                wire_sent here mirrors onto the Dashboard checklist. */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h2 className="text-lg font-bold text-black">Pay Cash to Close</h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Submit your remaining cash to close via wire transfer. Funds must be received at least 3 business days before your closing date.
                  </p>
                </div>
                <span
                  className={`ml-4 shrink-0 text-xs font-semibold px-3 py-1 rounded-full ${
                    funds_received
                      ? 'bg-green-100 text-green-700'
                      : wire_sent
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {funds_received
                    ? 'Funds Received ✓'
                    : wire_sent
                      ? 'Wire Sent Pending Verification'
                      : 'Pending'}
                </span>
              </div>

              {/* Amount Display — headline shows the net (Remaining to
                  Wire) when the borrower has already paid + admin has
                  verified an option-period down payment, otherwise the
                  gross. */}
              <div className="text-center py-6 bg-gray-50 rounded-xl mb-4">
                <p className="text-sm text-gray-500 mb-1">Amount Due</p>
                <p className="text-4xl font-bold text-black">{fmtMoney(ctcAmount)}</p>
              </div>

              {/* Cash-to-Close Breakdown — mirrors the Dashboard panel
                  so the borrower sees how the option-period down payment
                  was applied as a credit against the gross CTC. */}
              {dpSent > 0 && grossCtc != null && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 mb-4 text-sm">
                  <div className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-2">Cash to Close Breakdown</div>
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-600">Total Cash to Close</span>
                    <span className="font-mono text-black">{fmtMoney(grossCtc)}</span>
                  </div>
                  <div className="flex justify-between mb-1 text-green-700">
                    <span>Down Payment Already Sent</span>
                    <span className="font-mono">−{fmtMoney(dpSent)}</span>
                  </div>
                  <div className="flex justify-between border-t border-gray-200 mt-2 pt-2 font-bold text-black">
                    <span>Remaining to Wire</span>
                    <span className="font-mono">{fmtMoney(netCtc)}</span>
                  </div>
                </div>
              )}

              {/* Unverified-but-sent state — borrower has wired the down
                  payment but admin hasn't confirmed receipt yet, so the
                  gross is still what they owe. Matches the Dashboard's
                  yellow notice. */}
              {!data?.down_payment_verified && Number(data?.down_payment_amount_sent || 0) > 0 && (
                <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3 text-sm text-yellow-800">
                  Your down payment credit will be applied once confirmed by Doorly.
                </div>
              )}

              {/* Wire-sent confirmation banner */}
              {wire_sent && !funds_received && (
                <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3 text-sm text-yellow-800 font-medium">
                  Wire notification received — Doorly is confirming receipt with title.
                </div>
              )}
              {funds_received && (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-700 font-medium">
                  Cash to Close received and verified.
                </div>
              )}

              {/* Action buttons — mirror Dashboard exactly. Hide once the
                  wire has been submitted so the borrower doesn't double-fire. */}
              {!wire_sent && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    onClick={() => setShowWireCTC(true)}
                    className="py-3 rounded-xl text-white font-semibold text-sm transition-colors"
                    style={{ backgroundColor: '#3a6ad9' }}
                  >
                    Wire Instructions
                  </button>
                  <button
                    onClick={handleWireSent}
                    disabled={wireSending}
                    className="py-3 rounded-xl font-bold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: '#acf44e', color: '#1a1a1a' }}
                  >
                    {wireSending ? 'Confirming…' : 'I have sent the wire'}
                  </button>
                </div>
              )}
            </div>

          </div>
        )}
      </div>

      {/* CD Signing Modal */}
      {cdModal && (
        <div className="safe-modal-overlay fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-40 px-4">
          {/* 🔑 THE SAFE-AREA MODAL SHELL (index.css .safe-modal-*; fix/signing-scroll): capped to the visible viewport, the
              header and the Sign button never scroll, the middle is the popup's one vertical scroll. */}
          <div className="safe-modal-box safe-modal-shell bg-white rounded-2xl shadow-xl w-full max-w-md" data-testid="cd-sign-box">
            <div className="safe-modal-header flex items-center justify-between px-6 pt-6 pb-4">
              <h3 className="text-lg font-bold text-black">Sign Closing Disclosure</h3>
              <button
                onClick={() => {
                  setCdModal(false);
                  setCdCheck1(false);
                  setCdCheck2(false);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="safe-modal-body px-6" data-testid="cd-sign-body">
            {/* Checkboxes */}
            <div className="space-y-3 mb-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={cdCheck1}
                  onChange={(e) => setCdCheck1(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 accent-blue-600 cursor-pointer"
                />
                <span className="text-sm text-gray-700">
                  I confirm I have received and reviewed my Closing Disclosure
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={cdCheck2}
                  onChange={(e) => setCdCheck2(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 accent-blue-600 cursor-pointer"
                />
                <span className="text-sm text-gray-700">
                  I understand my final loan terms and closing costs
                </span>
              </label>
            </div>

            {/* Typed legal name */}
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Your full legal name</label>
              <input
                type="text"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                placeholder="As shown on the Closing Disclosure"
                disabled={signing}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Drawn signature */}
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-700 mb-2">Sign below</label>
              {signatureDataUrl ? (
                <div className="flex items-center gap-3">
                  <img src={signatureDataUrl} alt="Signature"
                    className="h-16 border border-gray-200 rounded bg-white p-1" />
                  <button type="button" onClick={() => setSignatureDataUrl(null)} disabled={signing}
                    className="px-3 py-2 rounded-lg border border-gray-300 text-xs font-semibold text-gray-700 bg-white">
                    Redo
                  </button>
                </div>
              ) : (
                <SignaturePad
                  width={420}
                  height={110}
                  confirmLabel="Save Signature"
                  onSave={setSignatureDataUrl}
                  disabled={signing}
                />
              )}
            </div>

            {signError && (
              <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700">
                {signError}
              </div>
            )}

            </div>
            <div className="safe-modal-footer px-6 pb-6">
            <button
              onClick={handleSignCD}
              disabled={!cdCheck1 || !cdCheck2 || !typedName.trim() || !signatureDataUrl || signing}
              className="w-full py-3 rounded-xl text-white font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#3a6ad9' }}
            >
              {signing ? 'Signing…' : 'Sign Closing Disclosure'}
            </button>
            </div>
          </div>
        </div>
      )}

      {/* Shared Wire Instructions Modal — same component the Dashboard
          uses for CTC, fed the same wire_instructions payload from
          /api/closing/status so both surfaces show identical wire info. */}
      <WireInstructionsModal
        isOpen={showWireCTC}
        onClose={() => setShowWireCTC(false)}
        loanNumber={data?.loan_number}
        wireType="ctc"
        amount={ctcAmount}
        wireData={data?.wire_instructions}
      />
    </AppShell>
  );
}
